import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar, Plus, Edit2, Trash2, PartyPopper, ChevronLeft, ChevronRight, RefreshCw, AlertCircle, CalendarDays, Repeat } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay } from "date-fns";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HolidayTypeBadge = ({ type }) => {
  const styles = {
    public: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    company: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    optional: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
  };
  
  return (
    <Badge className={styles[type] || styles.public}>
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </Badge>
  );
};

export default function HolidayCalendar() {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  
  const [holidayForm, setHolidayForm] = useState({
    name: "",
    date: "",
    description: "",
    is_recurring: false,
    holiday_type: "public"
  });

  const { businessId } = useBusiness();
  const isAdmin = currentUser?.role && ["super_admin", "admin", "manager"].includes(currentUser.role);

  useEffect(() => {
    fetchData();
  }, [selectedYear, businessId]);

  const fetchData = async () => {
    setLoading(true);
    const businessQuery = businessId ? `&business_id=${businessId}` : "";
    try {
      const [userRes, holidaysRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/attendance/holidays?year=${selectedYear}${businessQuery}`, { credentials: "include" })
      ]);

      if (userRes.ok) setCurrentUser(await userRes.json());
      if (holidaysRes.ok) setHolidays(await holidaysRes.json());
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setHolidayForm({
      name: "",
      date: "",
      description: "",
      is_recurring: false,
      holiday_type: "public"
    });
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    
    if (!holidayForm.name || !holidayForm.date) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/attendance/holidays`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(holidayForm)
      });
      
      if (res.ok) {
        toast.success("Holiday added successfully");
        setAddDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to add holiday");
      }
    } catch (error) {
      toast.error("Failed to add holiday");
    }
  };

  const handleEditHoliday = async (e) => {
    e.preventDefault();
    
    if (!holidayForm.name || !holidayForm.date) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/attendance/holidays/${selectedHoliday.holiday_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(holidayForm)
      });
      
      if (res.ok) {
        toast.success("Holiday updated successfully");
        setEditDialogOpen(false);
        setSelectedHoliday(null);
        resetForm();
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to update holiday");
      }
    } catch (error) {
      toast.error("Failed to update holiday");
    }
  };

  const handleDeleteHoliday = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/attendance/holidays/${selectedHoliday.holiday_id}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success("Holiday deleted successfully");
        setDeleteDialogOpen(false);
        setSelectedHoliday(null);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to delete holiday");
      }
    } catch (error) {
      toast.error("Failed to delete holiday");
    }
  };

  const openEditDialog = (holiday) => {
    setSelectedHoliday(holiday);
    setHolidayForm({
      name: holiday.name,
      date: holiday.date,
      description: holiday.description || "",
      is_recurring: holiday.is_recurring || false,
      holiday_type: holiday.holiday_type || "public"
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (holiday) => {
    setSelectedHoliday(holiday);
    setDeleteDialogOpen(true);
  };

  // Calendar view helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Add padding days for the week view
  const startDay = monthStart.getDay();
  const paddingDays = Array(startDay).fill(null);

  const getHolidaysForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return holidays.filter(h => {
      if (h.date === dateStr) return true;
      if (h.is_recurring) {
        const hDate = parseISO(h.date);
        return hDate.getMonth() === date.getMonth() && hDate.getDate() === date.getDate();
      }
      return false;
    });
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  const goToToday = () => setCurrentMonth(new Date());

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <PartyPopper className="w-7 h-7 text-amber-500" />
            Holiday Calendar
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage company holidays - excluded from leave calculations
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {isAdmin && (
            <Button onClick={() => setAddDialogOpen(true)} data-testid="add-holiday-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Holiday
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={prevMonth}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-lg font-semibold">
                {format(currentMonth, "MMMM yyyy")}
              </h2>
              <Button variant="ghost" size="icon" onClick={nextMonth}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
          </CardHeader>
          <CardContent>
            {/* Week headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                <div key={day} className="text-center text-xs font-medium text-slate-500 dark:text-slate-400 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {paddingDays.map((_, i) => (
                <div key={`pad-${i}`} className="aspect-square" />
              ))}
              
              {daysInMonth.map(day => {
                const dayHolidays = getHolidaysForDate(day);
                const hasHoliday = dayHolidays.length > 0;
                
                return (
                  <div
                    key={day.toISOString()}
                    data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                    className={`aspect-square p-1 rounded-lg border transition-colors ${
                      hasHoliday 
                        ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800" 
                        : "border-transparent hover:bg-slate-50 dark:hover:bg-slate-800"
                    } ${isToday(day) ? "ring-2 ring-indigo-500" : ""}`}
                  >
                    <div className={`text-xs font-medium ${
                      isToday(day) 
                        ? "text-indigo-600 dark:text-indigo-400" 
                        : hasHoliday 
                          ? "text-red-700 dark:text-red-400" 
                          : "text-slate-700 dark:text-slate-300"
                    }`}>
                      {format(day, "d")}
                    </div>
                    {dayHolidays.length > 0 && (
                      <div className="mt-0.5 truncate text-[10px] text-red-600 dark:text-red-400 font-medium">
                        {dayHolidays[0].name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Stats & Quick Info */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-red-500" />
                {selectedYear} Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600 dark:text-slate-400">Total Holidays</span>
                <Badge variant="secondary" className="text-lg">{holidays.length}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600 dark:text-slate-400">Public Holidays</span>
                <span className="font-medium">{holidays.filter(h => h.holiday_type === "public").length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600 dark:text-slate-400">Company Holidays</span>
                <span className="font-medium">{holidays.filter(h => h.holiday_type === "company").length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600 dark:text-slate-400">Recurring</span>
                <span className="font-medium flex items-center gap-1">
                  <Repeat className="w-3 h-3" />
                  {holidays.filter(h => h.is_recurring).length}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Upcoming Holidays</CardTitle>
            </CardHeader>
            <CardContent>
              {holidays
                .filter(h => h.date >= format(new Date(), "yyyy-MM-dd"))
                .slice(0, 5)
                .map(h => (
                  <div key={h.holiday_id} className="flex items-center justify-between py-2 border-b last:border-0 dark:border-slate-700">
                    <div>
                      <p className="font-medium text-sm">{h.name}</p>
                      <p className="text-xs text-slate-500">{format(parseISO(h.date), "MMM d, yyyy")}</p>
                    </div>
                    <HolidayTypeBadge type={h.holiday_type} />
                  </div>
                ))}
              {holidays.filter(h => h.date >= format(new Date(), "yyyy-MM-dd")).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No upcoming holidays</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Holiday List Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            All Holidays ({selectedYear})
          </CardTitle>
          <CardDescription>
            These holidays are automatically excluded when calculating leave duration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Holiday Name</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Recurring</TableHead>
                  <TableHead>Description</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-slate-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No holidays configured for {selectedYear}
                    </TableCell>
                  </TableRow>
                ) : (
                  holidays.map((holiday, index) => (
                    <TableRow key={holiday.holiday_id} data-testid={`holiday-row-${holiday.holiday_id}`}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell className="font-medium">{holiday.name}</TableCell>
                      <TableCell>
                        {format(parseISO(holiday.date), "MMM d, yyyy")}
                        <span className="ml-2 text-xs text-slate-500">
                          ({format(parseISO(holiday.date), "EEEE")})
                        </span>
                      </TableCell>
                      <TableCell>
                        <HolidayTypeBadge type={holiday.holiday_type} />
                      </TableCell>
                      <TableCell>
                        {holiday.is_recurring ? (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                            <Repeat className="w-3 h-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <span className="text-slate-400">No</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-slate-500">
                        {holiday.description || "-"}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(holiday)}
                              data-testid={`edit-holiday-${holiday.holiday_id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => openDeleteDialog(holiday)}
                              data-testid={`delete-holiday-${holiday.holiday_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add Holiday Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-500" />
              Add Holiday
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddHoliday} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Holiday Name *</Label>
              <Input
                id="name"
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                placeholder="e.g., Diwali"
                data-testid="holiday-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                data-testid="holiday-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="type">Holiday Type</Label>
              <Select
                value={holidayForm.holiday_type}
                onValueChange={(v) => setHolidayForm({ ...holidayForm, holiday_type: v })}
              >
                <SelectTrigger data-testid="holiday-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public Holiday</SelectItem>
                  <SelectItem value="company">Company Holiday</SelectItem>
                  <SelectItem value="optional">Optional Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Recurring Annually</Label>
                <p className="text-xs text-slate-500">Repeats every year on this date</p>
              </div>
              <Switch
                checked={holidayForm.is_recurring}
                onCheckedChange={(v) => setHolidayForm({ ...holidayForm, is_recurring: v })}
                data-testid="holiday-recurring-switch"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={holidayForm.description}
                onChange={(e) => setHolidayForm({ ...holidayForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
                data-testid="holiday-description-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-holiday-btn">
                Add Holiday
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Holiday Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-500" />
              Edit Holiday
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditHoliday} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Holiday Name *</Label>
              <Input
                id="edit-name"
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                data-testid="edit-holiday-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-date">Date *</Label>
              <Input
                id="edit-date"
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                data-testid="edit-holiday-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Holiday Type</Label>
              <Select
                value={holidayForm.holiday_type}
                onValueChange={(v) => setHolidayForm({ ...holidayForm, holiday_type: v })}
              >
                <SelectTrigger data-testid="edit-holiday-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public Holiday</SelectItem>
                  <SelectItem value="company">Company Holiday</SelectItem>
                  <SelectItem value="optional">Optional Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Recurring Annually</Label>
                <p className="text-xs text-slate-500">Repeats every year</p>
              </div>
              <Switch
                checked={holidayForm.is_recurring}
                onCheckedChange={(v) => setHolidayForm({ ...holidayForm, is_recurring: v })}
                data-testid="edit-holiday-recurring-switch"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={holidayForm.description}
                onChange={(e) => setHolidayForm({ ...holidayForm, description: e.target.value })}
                rows={2}
                data-testid="edit-holiday-description-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="update-holiday-btn">
                Update Holiday
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Holiday
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 dark:text-slate-400">
            Are you sure you want to delete <strong>{selectedHoliday?.name}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteHoliday} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
